// src/components/authentication/ProtectedRoute.js
/**
 * Route guard that blocks unauthenticated users.
 *
 * Usage
 * -----
 * ```jsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * Behaviour
 * ----------
 * • Reads the current user from our `AuthContext` hook.
 * • Reads the verified regional backend user from `RegionalBackendContext`.
 * • Signed-in users wait here until `/v1/users/me` has succeeded.
 * • If backend verification fails, we show a retry state instead of rendering
 *   protected content from Firebase client auth alone.
 * • Otherwise → `<Navigate replace to="/auth" />` to show the login flow.
 *
 * Props
 * @param {{ children: React.ReactElement }} props
 *   React element(s) to render once the user is authenticated.
 *
 * @returns {JSX.Element}
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useCurrentUserClinicMapping } from "../../context/UserClinicMappingContext";
import { useProfilePrefetch } from "../../context/ProfilePrefetchContext";
import { useRegionalBackend } from "../../context/RegionalBackendContext";
import { getSafeInternalNextPath } from "../../utils/navigation/recordingModeNav";
import {
  FORM_PATH,
  STUDY_ACCESS_PATH,
} from "../../utils/navigation/onboardingPaths";
import {
  resolveOnboardingRedirectPath,
  resolveOnboardingStage,
} from "../../utils/navigation/onboardingGate";
import RegionalSupportCodeNotice from "../backend/RegionalSupportCodeNotice";
import { getRegionalBackendSupportMetadata } from "../../utils/backend/regionalSupportCodes";
import Spinner from "../Spinner";

export function ProtectedRoute({
  children,
  loadingFallback = null,
  allowMissingStudyAccess = false,
  allowMissingMetadata = false,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const mapping = useCurrentUserClinicMapping();
  const { getCachedProfileInfo } = useProfilePrefetch();
  const { backendUser, loading, error, refresh, profileSummary } =
    useRegionalBackend();
  const cachedProfileInfo = user?.uid ? getCachedProfileInfo(user.uid) : null;
  const currentInternalPath = `${location.pathname}${location.search}`;
  const requestedNextPath = getSafeInternalNextPath(
    new URLSearchParams(location.search).get("next"),
    "/",
  );
  const isOnboardingRoute =
    location.pathname === STUDY_ACCESS_PATH ||
    location.pathname === FORM_PATH ||
    location.pathname === "/review-metadata";
  const onboardingNextPath = isOnboardingRoute
    ? requestedNextPath !== "/"
      ? requestedNextPath
      : null
    : currentInternalPath;
  const onboardingStage = resolveOnboardingStage({
    subjectId: mapping.subjectId,
    clinicStudyId: mapping.clinicStudyId,
    latestMetadataMs:
      cachedProfileInfo?.latestMs ?? profileSummary?.latestMs ?? null,
    isReturning: profileSummary?.returning === true,
  });

  const renderLoadingState = () => {
    if (loadingFallback) {
      return loadingFallback;
    }

    return (
      <div
        className="w-full min-h-52 flex flex-col items-center justify-center gap-3"
        role="status"
        aria-live="polite"
      >
        <Spinner />
      </div>
    );
  };

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (loading) {
    return renderLoadingState();
  }

  if (error) {
    const { errorCode, supportCode } = getRegionalBackendSupportMetadata(
      error,
      "ACCOUNT_ACCESS_ERROR",
    );

    return (
      <section
        aria-label="Regional backend account error"
        className="w-full max-w-lg m-auto p-4 flex flex-col items-start gap-3"
      >
        <h1 className="title-text">{t("regionalBackend.accountError.title")}</h1>
        <p>{t("regionalBackend.accountError.body")}</p>
        <RegionalSupportCodeNotice
          supportCode={supportCode}
          technicalCode={errorCode}
        />
        <button
          type="button"
          className="small-primary-button"
          onClick={refresh}
        >
          {t("regionalBackend.accountError.retry")}
        </button>
      </section>
    );
  }

  if (!backendUser || !mapping.resolved) {
    return renderLoadingState();
  }

  if (onboardingStage === "study-access" && !allowMissingStudyAccess) {
    return (
      <Navigate
        to={resolveOnboardingRedirectPath({
          stage: onboardingStage,
          search: location.search,
          next: onboardingNextPath,
        })}
        replace
      />
    );
  }

  if (onboardingStage === "metadata" && !allowMissingMetadata) {
    return (
      <Navigate
        to={resolveOnboardingRedirectPath({
          stage: onboardingStage,
          search: location.search,
          next: onboardingNextPath,
        })}
        replace
      />
    );
  }

  // Outstanding legal actions (e.g. an unacknowledged Terms update) are enforced
  // app-wide by <LegalUpdateOverlay>, not by a route redirect, so protected
  // routes render normally and the overlay blocks interaction on top.
  return children;
}
